import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { WeeklyScheduleGrid } from './weekly/WeeklyScheduleGrid';
import { WeeklyAgendaList } from './weekly/WeeklyAgendaList';
import { AthleteDayList } from './weekly/AthleteDayList';
import { ScheduledClass } from '@/types/types';
import { WeeklyDialogs } from './weekly/WeeklyDialogs';
import { useWeeklySchedule } from './hooks/useWeeklySchedule';
import { DuplicateScheduleDialog } from './DuplicateScheduleDialog';
import { useUserStore } from "@/store/userStore";
import { Skeleton } from "@/components/ui/skeleton";
import { useClientSubscriptionStore } from '@/store/api/clients/clientSubscriptionStore';
import { useClientsSupabaseStore } from '@/store/api/clientsSupabaseStore';
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { useDuplicateSchedule } from '@/store/schedule/useDuplicateSchedule';
import { isAfter } from 'date-fns';
import { scheduleClassesService } from '@/services/scheduleClassesService';
import { useSettingsStore } from '@/store/api/settingsStore';
import { useAccessibleCalendars } from '@/hooks/useAccessibleCalendars';
import {
  attachCalendarScopes,
  fetchUpcomingBookingsByCalendar,
  needsCalendarBreakdown,
  type WithCalendarScope,
} from '@/services/subscriptionCalendarsService';
import type { Subscription } from '@/types/subscription';

interface WeeklyScheduleProps {
  weekDates: Date[];
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onAddClass: (date: Date, time: string) => void;
  onEditClass: (classData: any, date: Date) => void;
  classes: ScheduledClass[];
  isLoading: boolean;
  isAdmin?: boolean;
  refreshClasses: () => Promise<void>;
  isClassBooked?: (classId: string, date: Date, time: string) => boolean;
  onMountRefreshCallback?: (refreshFn: () => Promise<void>) => void;
  realtimeModifiedSlots?: Set<string>;
  selectedCalendarId?: string | null;
  /** 'week' = grille hebdomadaire (défaut), 'agenda' = liste colorée par jour,
   *  'day' = liste des séances du jour sélectionné (portail athlète). */
  viewMode?: 'week' | 'agenda' | 'day';
}

export const WeeklySchedule: React.FC<WeeklyScheduleProps> = ({
  weekDates,
  selectedDate,
  onDateSelect,
  onAddClass,
  onEditClass,
  classes: propClasses,
  isLoading,
  isAdmin: propIsAdmin,
  refreshClasses,
  isClassBooked: propIsClassBooked,
  onMountRefreshCallback,
  realtimeModifiedSlots = new Set(),
  selectedCalendarId,
  viewMode = 'week'
}) => {
  const { t } = useTranslation(['schedule', 'common']);
  
  // Memoize les classes passées en props
  const classes = React.useMemo(() => {
    if (!propClasses || !Array.isArray(propClasses)) {
      console.warn('[WeeklySchedule] Invalid or empty classes array:', propClasses);
      return [];
    }
    return propClasses;
  }, [propClasses]);
  const { getClientActiveSubscriptionsByUserId } = useClientSubscriptionStore();
  const { role, user, gymId } = useUserStore();
  const { duplicateSchedule } = useDuplicateSchedule();
  const { settings } = useSettingsStore();
  const { accessibleCalendarIds, isLoading: isAccessibleCalendarsLoading } = useAccessibleCalendars(gymId ?? null);

  const isAdmin = propIsAdmin ?? role === 'admin';
  const isCoach = role === 'coach';
  const isClient = !isAdmin && !isCoach;

  const displayClasses = useMemo(() => {
    if (isAdmin || isCoach) return classes;
    if (isAccessibleCalendarsLoading) return classes;
    if (accessibleCalendarIds.length === 0) return [];
    return classes.filter((c) => {
      // Accès : un cours sans calendrier est visible par tous ; sinon il faut
      // que l'un des abonnements de l'adhérent ouvre ce calendrier.
      const accessible = c.calendar_id == null || accessibleCalendarIds.includes(c.calendar_id);
      if (!accessible) return false;
      // Bascule de planning côté adhérent : quand un calendrier précis est
      // sélectionné, on n'affiche que ses cours (les cours sans calendrier
      // restent visibles quel que soit l'onglet). `null` = tous les plannings.
      if (selectedCalendarId) {
        return c.calendar_id == null || c.calendar_id === selectedCalendarId;
      }
      return true;
    });
  }, [classes, isAdmin, isCoach, accessibleCalendarIds, isAccessibleCalendarsLoading, selectedCalendarId]);
  
  // Get booking advance days from settings (0 = unlimited)
  const bookingAdvanceDays = settings?.schedule?.bookingAdvanceDays || 0;
  
  // ✅ OPTIMISATION : Précharger les clients une seule fois au montage
  const { fetchClients } = useClientsSupabaseStore();
  
  useEffect(() => {
    if (propIsAdmin || role === 'coach') {
      console.log('[WeeklySchedule] Preloading clients for faster dialog opening...');
      fetchClients();
    }
  }, [propIsAdmin, role]);

  const [paused, setPaused] = useState(false);
  // `null` = valeur inconnue (lecture jamais aboutie), à ne pas confondre avec 0.
  const [sessionsRemaining, setSessionsRemaining] = useState<number | null>(null);
  const [upcomingClassesCount, setUpcomingClassesCount] = useState<number | null>(null);
  const [endDate, setEndDate] = useState("");
  // TOUS les abonnements actifs, enrichis des calendriers que leur type ouvre.
  // Un adhérent peut en détenir plusieurs, de plannings différents, avec des
  // quotas et des dates de fin indépendants : les trois scalaires ci-dessus ne
  // décrivent que le PREMIER et ne servent plus qu'au repli historique.
  // `null` = lecture jamais aboutie (statut inconnu, ≠ « aucun abonnement »).
  const [activeSubscriptions, setActiveSubscriptions] =
    useState<WithCalendarScope<Subscription>[] | null>(null);
  // Ventilation des réservations à venir par `calendar_id`. `null` = non
  // ventilé (salle sans multi-planning, ou reconstruction en échec) : le
  // dialogue retombe alors sur le comptage global, comme avant.
  const [upcomingByCalendar, setUpcomingByCalendar] =
    useState<Record<string, number> | null>(null);
  // Vrai uniquement quand on n'a jamais réussi à lire l'abonnement de cet
  // utilisateur : un échec ponctuel après un chargement réussi conserve les
  // dernières valeurs connues (pas de clignotement sur refresh silencieux).
  const [clientDataUnavailable, setClientDataUnavailable] = useState(false);
  const subscriptionLoadedForUserRef = React.useRef<string | null>(null);
  const [isClientDataLoading, setIsClientDataLoading] = useState(true);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  
  // ✅ État pour tracker les créneaux récemment réservés (succès visuel)
  const [recentlyBookedSlots, setRecentlyBookedSlots] = useState<Set<string>>(new Set());
  
  const addRecentlyBookedSlot = useCallback((classId: string, date: string, time: string) => {
    const slotKey = `${classId}-${date}-${time}`;
    setRecentlyBookedSlots(prev => new Set(prev).add(slotKey));
    
    // Retirer après 2 secondes
    setTimeout(() => {
      setRecentlyBookedSlots(prev => {
        const next = new Set(prev);
        next.delete(slotKey);
        return next;
      });
    }, 2000);
  }, []);

  const fetchClientData = React.useCallback(async (silent = false) => {
    if (!user?.id || !gymId) {
      // `isClientDataLoading` vaut `true` à l'initialisation, et le rendu s'en
      // sert pour afficher un squelette PLEIN ÉCRAN à l'adhérent. Sortir d'ici
      // sans le redescendre — ce que faisaient les deux `return` nus — figeait
      // l'écran sur ce squelette indéfiniment dès que la salle n'était pas
      // encore résolue : il n'y a plus rien à charger, mais plus rien non plus
      // pour éteindre le voyant.
      setIsClientDataLoading(false);
      return;
    }

    const userId = user.id;

    // ✅ Ne pas mettre isLoading si c'est un refresh silencieux
    if (!silent) {
      setIsClientDataLoading(true);
    }

    try {
      const { getUserUpcomingBookingsCount } = await import(
        '@/services/edgeFunctionWrappers/bookingsManagementWrapper'
      );

      // `allSettled` et non `all` : avec `all`, l'échec du seul comptage des
      // réservations jetait aussi l'abonnement pourtant lu avec succès (et
      // inversement), ce qui faisait retomber le quota affiché à 0.
      const [subscriptionOutcome, upcomingOutcome] = await Promise.allSettled([
        // TOUS les abonnements actifs, et non le premier : c'est ce qui permet
        // à un adhérent de détenir « Planning général » ET « Cours Privés »
        // avec des quotas indépendants. `attachCalendarScopes` n'émet aucune
        // requête supplémentaire quand aucun abonnement n'est typé — le cas
        // des salles sans multi-planning.
        getClientActiveSubscriptionsByUserId(userId).then((subs) =>
          attachCalendarScopes(subs ?? [], gymId)
        ),
        getUserUpcomingBookingsCount(gymId, userId),
      ]);

      if (subscriptionOutcome.status === 'fulfilled') {
        const subscriptions = subscriptionOutcome.value;
        // Lecture aboutie : l'absence d'abonnement actif vaut bien 0 séance.
        setActiveSubscriptions(subscriptions);
        // Scalaires historiques, conservés pour le repli des appelants qui ne
        // reçoivent pas la liste. Ils décrivent le premier abonnement actif,
        // exactement comme avant.
        const first = subscriptions[0];
        setPaused(first?.is_paused || false);
        setSessionsRemaining(first?.sessions_remaining ?? 0);
        setEndDate(first?.end_date ?? "");
        subscriptionLoadedForUserRef.current = userId;
        setClientDataUnavailable(false);
      } else {
        console.error(
          "[WeeklySchedule] Lecture de l'abonnement en échec:",
          subscriptionOutcome.reason
        );
        // « Inconnu » n'est pas « zéro » : on n'écrase jamais un quota déjà
        // connu par 0, sinon une panne réseau ferait refuser une réservation
        // parfaitement légitime. Si un chargement précédent a réussi pour cet
        // utilisateur on garde ses valeurs telles quelles (et l'écran ne
        // clignote pas à chaque rafraîchissement de fond) ; sinon on signale
        // explicitement l'indisponibilité au dialogue de réservation.
        if (subscriptionLoadedForUserRef.current !== userId) {
          setPaused(false);
          setSessionsRemaining(null);
          setEndDate("");
          setActiveSubscriptions(null);
          setClientDataUnavailable(true);
        }
      }

      if (upcomingOutcome.status === 'fulfilled' && !upcomingOutcome.value.error) {
        setUpcomingClassesCount(upcomingOutcome.value.data?.upcomingCount ?? 0);
      } else {
        console.error(
          '[WeeklySchedule] Comptage des réservations à venir en échec:',
          upcomingOutcome.status === 'rejected'
            ? upcomingOutcome.reason
            : upcomingOutcome.value.error
        );
        // On conserve le dernier comptage connu (ou `null` s'il n'y en a
        // jamais eu) plutôt que de le forcer à 0.
      }

      // Ventilation du comptage par calendrier. Indispensable dès qu'un
      // abonnement est restreint : sans elle, six réservations de Pilates
      // épuiseraient le quota « Salle », le comptage global étant comparé au
      // quota d'un seul planning. Inutile — et donc non déclenchée — quand
      // tous les abonnements sont universels : le total global est alors la
      // bonne réponse, au comportement d'aujourd'hui près.
      if (subscriptionOutcome.status === 'fulfilled') {
        if (needsCalendarBreakdown(subscriptionOutcome.value)) {
          try {
            setUpcomingByCalendar(await fetchUpcomingBookingsByCalendar(gymId));
          } catch (breakdownError) {
            console.error(
              '[WeeklySchedule] Ventilation des réservations par planning en échec, repli sur le total:',
              breakdownError
            );
            setUpcomingByCalendar(null);
          }
        } else {
          setUpcomingByCalendar(null);
        }
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      if (subscriptionLoadedForUserRef.current !== userId) {
        setClientDataUnavailable(true);
      }
    } finally {
      setIsClientDataLoading(false);
    }
  }, [user?.id, gymId, getClientActiveSubscriptionsByUserId]);

  // Rafraîchissement à la demande depuis le dialogue de réservation : silencieux
  // pour ne pas remplacer le planning par le squelette de chargement.
  const retryClientData = React.useCallback(() => fetchClientData(true), [fetchClientData]);


  // Initial mount: fetch once
  useEffect(() => {
    if (!isAdmin && !isCoach) {
      fetchClientData();
    }
  }, [isAdmin, isCoach, fetchClientData, user?.id]);

  // ✅ Exposer fetchClientData au parent pour refresh silencieux
  useEffect(() => {
    if (onMountRefreshCallback && !isAdmin && !isCoach) {
      onMountRefreshCallback(() => fetchClientData(true));
    }
  }, [onMountRefreshCallback, isAdmin, isCoach, fetchClientData]);

  // ✅ HOOKS: Appel avant tous les early returns
  const {
    isDetailsDialogOpen,
    setIsDetailsDialogOpen,
    selectedClassDetails,
    selectedClassDate,
    isBookingDialogOpen,
    setIsBookingDialogOpen,
    selectedBookingClass,
    selectedBookingSession,
    isEditDialogOpen,
    setIsEditDialogOpen,
    editClassInfo,
    editClassDate,
    getClassForTimeSlot,
    getClassesForTimeSlot,
    handleClassClick: originalHandleClassClick,
    handleCapacityUpdate,
    handleDeleteClass,
    handleOpenEditDialog,
    handleSaveEditedClass,
    refreshClasses: hookRefreshClasses
  } = useWeeklySchedule(isAdmin || isCoach, displayClasses, refreshClasses);

  // Wrapper for handleClassClick to check booking advance days limit
  const handleClassClick = (classInfo: any, date: Date) => {
    originalHandleClassClick(classInfo, date);
  };
  
  // Utiliser la fonction passée en props ou fallback vers false
  const isClassBooked = propIsClassBooked || (() => false);

  // EARLY RETURNS: Après tous les hooks
  // Si c'est un client et que les données sont en cours de chargement
  if (!isAdmin && !isCoach && isClientDataLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center space-y-4">
          <Skeleton className="h-8 w-64 mx-auto" />
          <Skeleton className="h-4 w-48 mx-auto" />
        </div>
      </div>
    );
  }

  const handleDuplicateSchedule = async (config: any) => {
    // Aucun toast ici, et surtout aucun `catch` : c'est
    // `DuplicateScheduleDialog.handleConfirm` qui informe l'utilisateur et ne
    // referme le dialogue qu'en cas de succès. En avalant l'erreur, cette
    // fonction résolvait sa promesse même quand la duplication avait échoué :
    // le dialogue affichait alors « Planning dupliqué avec succès » par-dessus
    // le toast d'erreur, puis se refermait sans que rien n'ait été dupliqué
    // (et empilait deux toasts de succès quand elle réussissait).
    await duplicateSchedule(config);

    // La duplication est acquise à partir d'ici : un échec du rafraîchissement
    // ne doit pas être remonté comme un échec de duplication.
    try {
      if (user?.id && gymId) {
        scheduleClassesService.invalidateCache(user.id, gymId);
      }
      await refreshClasses();
    } catch (refreshError) {
      console.error('[WeeklySchedule] Duplication effectuée mais rafraîchissement en échec:', refreshError);
    }

    setIsDuplicateDialogOpen(false);
  };

  if (isLoading && classes.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-4">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Header avec bouton de duplication pour les admins */}
      {isAdmin && viewMode === 'week' && (
        <div className="flex justify-end mb-3 md:mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsDuplicateDialogOpen(true)}
            className="flex items-center gap-1.5 text-xs md:text-sm"
          >
            <Copy className="h-3.5 w-3.5 md:h-4 md:w-4" />
            {t('schedule.duplicateWeek', { defaultValue: 'Dupliquer la semaine' })}
          </Button>
        </div>
      )}

      {viewMode === 'day' ? (
        <AthleteDayList
          date={selectedDate}
          classes={displayClasses}
          onClassClick={handleClassClick}
          isClassBooked={isClassBooked}
          recentlyBookedSlots={recentlyBookedSlots}
          bookingAdvanceDays={isAdmin || isCoach ? 0 : bookingAdvanceDays}
        />
      ) : viewMode === 'agenda' ? (
        <WeeklyAgendaList
          weekDates={weekDates}
          classes={displayClasses}
          onClassClick={handleClassClick}
          isClassBooked={isClassBooked}
          recentlyBookedSlots={recentlyBookedSlots}
          manageMode={isAdmin || isCoach}
        />
      ) : (
        <WeeklyScheduleGrid
          weekDates={weekDates}
          selectedDate={selectedDate}
          onDateSelect={onDateSelect}
          onAddClass={onAddClass}
          onEditClass={onEditClass}
          isAdmin={isAdmin||isCoach}
          isLoading={isLoading}
          getClassForTimeSlot={getClassForTimeSlot}
          getClassesForTimeSlot={getClassesForTimeSlot}
          onHandleClassClick={handleClassClick}
          isClassBooked={isClassBooked}
          recentlyBookedSlots={recentlyBookedSlots}
          realtimeModifiedSlots={realtimeModifiedSlots}
          bookingAdvanceDays={isAdmin || isCoach ? 0 : bookingAdvanceDays}
          uniformMemberLayout={isClient}
        />
      )}

      <WeeklyDialogs
        isAdmin={isAdmin||isCoach}
        isDetailsDialogOpen={isDetailsDialogOpen}
        setIsDetailsDialogOpen={setIsDetailsDialogOpen}
        selectedClassDetails={selectedClassDetails}
        selectedClassDate={selectedClassDate}
        isBookingDialogOpen={isBookingDialogOpen}
        setIsBookingDialogOpen={setIsBookingDialogOpen}
        selectedBookingClass={selectedBookingClass}
        selectedBookingSession={selectedBookingSession}
        onCapacityUpdate={handleCapacityUpdate}
        handleDeleteClass={handleDeleteClass}
        clientCoursesBooked={upcomingClassesCount}
        clientRemainingSessions={sessionsRemaining}
        clientSubscriptionEndDate={endDate}
        clientActiveSubscriptions={activeSubscriptions}
        clientUpcomingByCalendar={upcomingByCalendar}
        clientDataUnavailable={clientDataUnavailable}
        onRetryClientData={retryClientData}
        refreshClasses={hookRefreshClasses}
        addRecentlyBookedSlot={addRecentlyBookedSlot}
        isEditDialogOpen={isEditDialogOpen}
        onEditDialogOpenChange={setIsEditDialogOpen}
        editClassInfo={editClassInfo}
        editClassDate={editClassDate}
        onSaveEditedClass={handleSaveEditedClass}
        onOpenEditDialog={handleOpenEditDialog}
        classes={displayClasses}
      />

      <DuplicateScheduleDialog
        isOpen={isDuplicateDialogOpen}
        onClose={() => setIsDuplicateDialogOpen(false)}
        onDuplicate={handleDuplicateSchedule}
        currentWeekDate={weekDates[0]}
        selectedCalendarId={selectedCalendarId}
      />
    </div>
  );
};
